import {
  parseFrontmatter,
  requireString,
  requireStringArray,
} from "./frontmatter";
import { isHelpCategory, type HelpCategoryId } from "./categories";
import { HELP_MARK_TYPES, isHelpMarkType } from "./marks";

/** Portal settings an article can be shown behind, each a switch on GET /portal-settings. */
export const HELP_ARTICLE_SETTINGS = ["portfolioBuilderEnabled", "portfolioTrackerEnabled"] as const;

export type HelpArticleSetting = (typeof HELP_ARTICLE_SETTINGS)[number];

const isHelpArticleSetting = (value: unknown): value is HelpArticleSetting =>
  HELP_ARTICLE_SETTINGS.includes(value as HelpArticleSetting);

export interface ValidatedArticle {
  slug: string;
  title: string;
  category: HelpCategoryId;
  summary: string;
  keywords: string[];
  order: number;
  updated: string;
  /** The setting that must be on for the article to be shown, or null when it always is. */
  requires: HelpArticleSetting | null;
  body: string;
}

/**
 * The single definition of what a valid help article is.
 *
 * Imported by help-content at runtime and by the Vite plugin at build time, so
 * there is one implementation rather than a TypeScript one and a build-script
 * one that drift. Everything here is pure — no import.meta.glob, no DOM — so it
 * can run in plain Node from vite.config.ts.
 */

/** Enough keywords that search has something to work with beyond the title. */
const MIN_KEYWORDS = 3;

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function validateArticle(
  file: string,
  source: string
): ValidatedArticle {
  const { data, body } = parseFrontmatter(source);

  const slug = requireString(data, "slug", file);
  if (!SLUG_PATTERN.test(slug)) {
    throw new Error(
      `Help article ${file}: slug "${slug}" must be lowercase and hyphenated ` +
        `(matching the existing /how-it-works convention).`
    );
  }

  const category = requireString(data, "category", file);
  if (!isHelpCategory(category)) {
    throw new Error(
      `Help article ${file}: unknown category "${category}". ` +
        `Add it to HELP_CATEGORIES or use an existing one.`
    );
  }

  const keywords = requireStringArray(data, "keywords", file);
  if (keywords.length < MIN_KEYWORDS) {
    throw new Error(
      `Help article ${file}: needs at least ${MIN_KEYWORDS} keywords, has ` +
        `${keywords.length}. Keywords are how search finds an article by a ` +
        `phrasing its title does not use.`
    );
  }

  if (!body.trim()) {
    throw new Error(`Help article ${file}: has frontmatter but no content.`);
  }

  const requires = data.requires ?? null;
  if (requires !== null && !isHelpArticleSetting(requires)) {
    throw new Error(
      `Help article ${file}: requires "${requires}" is not a portal setting ` +
        `it can be shown behind. Known settings are ${HELP_ARTICLE_SETTINGS.join(", ")}.`
    );
  }

  return {
    slug,
    title: requireString(data, "title", file),
    category,
    summary: requireString(data, "summary", file),
    keywords,
    order: Number(data.order ?? 0),
    updated: requireString(data, "updated", file),
    requires,
    body,
  };
}

export interface ImageReference {
  src: string;
  alt: string;
}

const IMAGE_PATTERN = /!\[([^\]]*)\]\(\s*([^)\s]+)/g;

/**
 * Local image references in an article body, for the build to check exist.
 *
 * Vite cannot do this for us: article bodies are loaded as raw strings, so a
 * broken image path is invisible until it 404s in front of a customer.
 */
export function extractImageReferences(body: string): ImageReference[] {
  const references: ImageReference[] = [];

  for (const match of body.matchAll(IMAGE_PATTERN)) {
    const src = match[2];
    if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) continue;
    references.push({ src: src.replace(/^\.\//, ""), alt: match[1] });
  }

  return references;
}

export function assertImagesUsable(
  file: string,
  references: ImageReference[],
  exists: (src: string) => boolean
): void {
  for (const { src, alt } of references) {
    if (!alt.trim()) {
      throw new Error(
        `Help article ${file}: image "${src}" has no alt text. Screenshots ` +
          `carry meaning here, so alt is required.`
      );
    }
    if (!exists(src)) {
      throw new Error(
        `Help article ${file}: image "${src}" does not exist. Paths are ` +
          `relative to src/content/help, and images live under ` +
          `images/<article-slug>/ — e.g. images/traderspost-setup/webhook.png.`
      );
    }
  }
}

/**
 * A published tooltip URL cannot be recalled, so two articles claiming one slug
 * has to be a hard failure rather than last-write-wins.
 */
export function assertUniqueSlugs(
  articles: { file: string; slug: string }[]
): void {
  const seen = new Map<string, string>();

  for (const { file, slug } of articles) {
    const clash = seen.get(slug);
    if (clash) {
      throw new Error(
        `Help article ${file}: slug "${slug}" is already used by ${clash}.`
      );
    }
    seen.set(slug, file);
  }
}

const MARK_PATTERN = /:mark\{([^}]*)\}/g;
const MARK_TYPE = /\btype=("[^"]*"|[^\s}]*)/;

/**
 * Card marks an article asks for, for the build to check it can draw.
 *
 * An unknown name renders nothing at all, so without this the section that
 * explains the icons would quietly ship with no icons in it.
 */
export function extractMarkTypes(body: string): string[] {
  return [...body.matchAll(MARK_PATTERN)].map(
    (match) => MARK_TYPE.exec(match[1])?.[1].replace(/"/g, "").trim() ?? ""
  );
}

export function assertMarksKnown(file: string, types: string[]): void {
  for (const type of types) {
    if (isHelpMarkType(type)) continue;
    throw new Error(
      `Help article ${file}: unknown card mark "${type}". ` +
        `Known marks are ${HELP_MARK_TYPES.join(", ")}.`
    );
  }
}
